import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button, Col, Row, Card, Space, Input, Typography, Modal, message } from 'antd';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { darcula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import { 
    SendOutlined, 
    BookOutlined, 
    ExperimentOutlined, 
    QuestionCircleOutlined, 
    EyeOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    RedoOutlined,
    DeleteOutlined,
    SaveOutlined,
    DownOutlined
} from '@ant-design/icons';
import { getBaseURL } from '../config.js';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { useMediaQuery } from 'react-responsive';
import ContextMenu from './ContextMenu';

const { Title, Text } = Typography;

const Chat = ({ selectedKey, setSelectedKey, course_id, userPreferences }) => {
    const [paused, setPaused] = useState(false);
    const { TextArea } = Input;
    const [ws, setWs] = useState(null);
    const [cards, setCards] = useState([]);
    const [language, setLanguage] = useState(userPreferences?.language || "English");
    const [depth, setDepth] = useState(userPreferences?.depth || "Medium");
    const baseURL = getBaseURL();
    const [inputValue, setInputValue] = useState("");
    const [showInput, setShowInput] = useState(false);
    const [userInputs, setUserInputs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [regeneratingIndex, setRegeneratingIndex] = useState(-1);
    const isSmallScreen = useMediaQuery({ maxWidth: 768 });
    const fontSize = isSmallScreen ? '14px' : '16px';
    const chatEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const [userScrolled, setUserScrolled] = useState(false);
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);

    const [contextMenuVisible, setContextMenuVisible] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });

    // 生成聊天记录的存储key
    const getChatStorageKey = useCallback(() => `chat_${course_id}_${selectedKey}`, [course_id, selectedKey]);

    // 保存聊天记录到localStorage
    const saveChatToStorage = useCallback((cardsData, userInputsData) => {
        try {
            const chatData = {
                cards: cardsData,
                userInputs: userInputsData,
                timestamp: Date.now(),
                selectedKey,
                course_id,
                language,
                depth
            };
            localStorage.setItem(getChatStorageKey(), JSON.stringify(chatData));
            console.log('Chat saved to storage');
        } catch (error) {
            console.error('Error saving chat to storage:', error);
        }
    }, [course_id, selectedKey, language, depth, getChatStorageKey]);

    // 从localStorage恢复聊天记录
    const loadChatFromStorage = useCallback(() => {
        try {
            const storageKey = getChatStorageKey();
            const savedChat = localStorage.getItem(storageKey);
            if (savedChat) {
                const chatData = JSON.parse(savedChat);
                setCards(chatData.cards || []);
                setUserInputs(chatData.userInputs || []);
                console.log('Chat loaded from storage');
                return true;
            }
        } catch (error) {
            console.error('Error loading chat from storage:', error);
        }
        return false;
    }, [getChatStorageKey]);

    // 清除聊天记录
    const clearChatHistory = () => {
        Modal.confirm({
            title: 'Clear Chat History',
            content: 'Are you sure you want to clear all chat history for this topic? This action cannot be undone.',
            okText: 'Clear',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk() {
                setCards([]);
                setUserInputs([]);
                localStorage.removeItem(getChatStorageKey());
                message.success('Chat history cleared');
            },
        });
    };

    // 检测用户是否手动滚动
    const handleScroll = useCallback(() => {
        if (!chatContainerRef.current) return;
        
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 50; // 50px tolerance
        
        setUserScrolled(!isAtBottom);
        setShowScrollToBottom(!isAtBottom && cards.length > 0);
    }, [cards.length]);

    // 智能滚动到底部
    const scrollToBottom = useCallback((force = false) => {
        if (!chatEndRef.current) return;
        
        // 只有在用户没有手动滚动或强制滚动时才自动滚动
        if (!userScrolled || force) {
            chatEndRef.current.scrollIntoView({ 
                behavior: "smooth",
                block: "end"
            });
        }
    }, [userScrolled]);

    // 强制滚动到底部（用户点击按钮时）
    const forceScrollToBottom = () => {
        setUserScrolled(false);
        scrollToBottom(true);
    };

    // 监听滚动事件
    useEffect(() => {
        const container = chatContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll, { passive: true });
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    // 只在新消息开始时自动滚动，内容更新时不强制滚动
    useEffect(() => {
        // 只有在新卡片添加时才滚动，而不是内容更新时
        const lastCard = cards[cards.length - 1];
        if (lastCard === "" && !userScrolled) {
            // 新消息开始，滚动到底部
            setTimeout(() => scrollToBottom(), 100);
        }
    }, [cards.length, scrollToBottom, userScrolled]);

    // 当selectedKey改变时，加载对应的聊天记录
    useEffect(() => {
        if (selectedKey) {
            const loaded = loadChatFromStorage();
            if (!loaded) {
                // 如果没有保存的记录，清空当前内容
                setCards([]);
                setUserInputs([]);
            }
        }
    }, [selectedKey, loadChatFromStorage]);

    // 保存聊天记录（当cards或userInputs改变时）
    useEffect(() => {
        if (cards.length > 0 || userInputs.length > 0) {
            saveChatToStorage(cards, userInputs);
        }
    }, [cards, userInputs, saveChatToStorage]);

    // 当用户偏好改变时更新语言和深度设置
    useEffect(() => {
        if (userPreferences) {
            setLanguage(userPreferences.language || "English");
            setDepth(userPreferences.depth || "Medium");
        }
    }, [userPreferences]);

    function getRecentContentUpToLimit(cards, limit = 4000) {
        let accumulatedContent = '';
        let index = cards.length - 1;

        while (index >= 0 && accumulatedContent.length < limit) {
            accumulatedContent = accumulatedContent + cards[index];
            index--;
        }

        return accumulatedContent.substr(0, limit);
    }

    const createWebSocket = useCallback(() => {
        console.log('Creating WebSocket connection to:', `wss://${baseURL}/gpt-response`);
        const ws = new WebSocket(`wss://${baseURL}/gpt-response`);

        ws.onopen = () => {
            console.log("WebSocket opened successfully");
            setIsLoading(false);
        };

        ws.onmessage = (event) => {
            if (paused) {
                console.log("Message received but paused, ignoring");
                return;
            }

            console.log("WebSocket message received:", event.data.substring(0, 100) + "...");
            setCards(prevCards => {
                const lastCardIndex = prevCards.length - 1;
                const newCards = [...prevCards];
                newCards[lastCardIndex] = newCards[lastCardIndex] + event.data;
                return newCards;
            });
            setIsLoading(false);
        };

        ws.onerror = (error) => {
            console.error("WebSocket error:", error);
            message.error('Connection error. Please check your internet connection.');
        };

        ws.onclose = (event) => {
            console.log("WebSocket closed:", event.code, event.reason);
            setShowInput(true);
            setIsLoading(false);
            setRegeneratingIndex(-1);
        };

        setWs(ws);
        return ws;
    }, [baseURL, paused]);

    useEffect(() => {
        console.log('Setting up WebSocket connection...');
        const ws = createWebSocket();
        return () => {
            console.log('Cleaning up WebSocket connection...');
            ws.close();
        };
    }, [createWebSocket]);

    const sendMessage = (params, functionName, isRegeneration = false, regenerationIndex = -1) => {
        console.log('sendMessage called:', {
            functionName,
            params,
            isRegeneration,
            regenerationIndex,
            wsState: ws?.readyState
        });
        
        setPaused(false);
        setIsLoading(true);
        
        if (isRegeneration) {
            setRegeneratingIndex(regenerationIndex);
        }
        
        if (ws && ws.readyState === WebSocket.OPEN) {
            const message = JSON.stringify({
                functionName: functionName,
                params: {
                    ...params,
                    course_id,
                },
            });
            
            console.log('Sending WebSocket message:', message);
            ws.send(message);
            
            if (isRegeneration) {
                // 重新生成时，替换指定位置的内容
                setCards(prevCards => {
                    const newCards = [...prevCards];
                    newCards[regenerationIndex] = "";
                    return newCards;
                });
            } else {
                // 正常生成时，添加新的卡片
                setCards(prevCards => [...prevCards, ""]);
            }
            
            setLastMessage(message);
            console.log('Message sent successfully');
        } else {
            console.error('Cannot send message: WebSocket not ready', {
                hasWs: !!ws,
                wsState: ws?.readyState,
                expectedState: WebSocket.OPEN
            });
            setIsLoading(false);
            message.error('Connection not ready. Please wait a moment and try again.');
        }
    };

    const [lastMessage, setLastMessage] = useState(null);

    const handleButtonClick = () => {
        console.log('handleButtonClick called', {
            wsState: ws?.readyState,
            inputValue: inputValue.trim(),
            selectedKey,
            hasWs: !!ws
        });
        
        if (!ws) {
            console.error('WebSocket not available');
            message.error('Connection not available. Please refresh the page.');
            return;
        }
        
        if (ws.readyState !== WebSocket.OPEN) {
            console.error('WebSocket not open, state:', ws.readyState);
            message.error('Connection not ready. Please wait a moment and try again.');
            return;
        }
        
        if (!inputValue.trim()) {
            console.error('Input value is empty');
            message.warning('Please enter a question.');
            return;
        }
        
        if (!selectedKey) {
            console.error('No topic selected');
            message.warning('Please select a topic first.');
            return;
        }

        try {
            const recentContent = getRecentContentUpToLimit(cards);
            console.log('Sending follow-up message:', {
                topic: selectedKey,
                depth: depth,
                language: language,
                question: inputValue,
                recentContentLength: recentContent.length
            });
            
            sendMessage({ 
                topic: selectedKey, 
                depth: depth, 
                language: language, 
                question: inputValue, 
                recentContent: recentContent 
            }, "follow_up");
            
            setUserInputs(prevInputs => [...prevInputs, { message: inputValue }]);
            setInputValue("");
            setShowInput(false);
            
            console.log('Follow-up message sent successfully');
        } catch (error) {
            console.error('Error sending message:', error);
            message.error('Failed to send message. Please try again.');
        }
    };

    const handleStopOutput = () => {
        setPaused(true);
        setIsLoading(false);
        setRegeneratingIndex(-1);
    };

    const handleRestartOutput = () => {
        setPaused(false);
        if (lastMessage) {
            sendMessage(lastMessage, "user_lesson");
        }
    };

    const handleContextMenu = (event) => {
        event.preventDefault();
        setContextMenuVisible(true);
        setContextMenuPosition({ x: event.clientX, y: event.clientY });
    };

    const handleClarify = (text) => {
        setInputValue(`Could you please clarify '${text}'`);
        handleButtonClick();
    };

    // 重新生成指定卡片的内容
    const handleRegenerate = (index) => {
        if (!userInputs[index]) return;
        
        Modal.confirm({
            title: 'Regenerate Content',
            content: 'Are you sure you want to regenerate this response? The current content will be replaced.',
            okText: 'Regenerate',
            cancelText: 'Cancel',
            onOk() {
                const userInput = userInputs[index];
                const recentContent = getRecentContentUpToLimit(cards.slice(0, index));
                
                // 根据用户输入的类型决定调用哪个函数
                let functionName = "follow_up";
                let params = { topic: selectedKey, depth: depth, language: language, question: userInput.message, recentContent: recentContent };
                
                if (userInput.message === "Start Lesson") {
                    functionName = "user_lesson";
                    params = { topic: selectedKey, depth: depth, language: language };
                } else if (userInput.message === "Start Example") {
                    functionName = "user_test";
                    params = { topic: selectedKey, depth: depth, language: language };
                } else if (userInput.message === "Start Quiz") {
                    functionName = "user_quiz";
                    params = { topic: selectedKey, depth: depth, language: language };
                } else if (userInput.message === "Start Review") {
                    functionName = "user_review";
                    params = { topic: selectedKey, depth: depth, language: language };
                }
                
                sendMessage(params, functionName, true, index);
            },
        });
    };

    // 删除指定的对话
    const handleDelete = (index) => {
        Modal.confirm({
            title: 'Delete Conversation',
            content: 'Are you sure you want to delete this conversation? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk() {
                setCards(prevCards => prevCards.filter((_, i) => i !== index));
                setUserInputs(prevInputs => prevInputs.filter((_, i) => i !== index));
                message.success('Conversation deleted');
            },
        });
    };

    const actionButtons = [
        {
            key: 'learn',
            label: 'Learn',
            icon: <BookOutlined />,
            type: 'primary',
            color: '#1890ff',
            action: () => {
                sendMessage({ topic: selectedKey, depth: depth, language: language }, "user_lesson");
                setUserInputs(prevInputs => [...prevInputs, { message: "Start Lesson" }]);
                setShowInput(false);
            }
        },
        {
            key: 'example',
            label: 'Example',
            icon: <ExperimentOutlined />,
            type: 'default',
            color: '#52c41a',
            action: () => {
                sendMessage({ topic: selectedKey, depth: depth, language: language }, "user_test");
                setUserInputs(prevInputs => [...prevInputs, { message: "Start Example" }]);
                setShowInput(false);
            }
        },
        {
            key: 'quiz',
            label: 'Quiz',
            icon: <QuestionCircleOutlined />,
            type: 'default',
            color: '#faad14',
            action: () => {
                sendMessage({ topic: selectedKey, depth: depth, language: language }, "user_quiz");
                setUserInputs(prevInputs => [...prevInputs, { message: "Start Quiz" }]);
                setShowInput(false);
            }
        },
        {
            key: 'review',
            label: 'Review',
            icon: <EyeOutlined />,
            type: 'default',
            color: '#722ed1',
            action: () => {
                sendMessage({ topic: selectedKey, depth: depth, language: language }, "user_review");
                setUserInputs(prevInputs => [...prevInputs, { message: "Start Review" }]);
                setShowInput(false);
            }
        }
    ];

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* 用户偏好显示 */}
            {userPreferences && (
                <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
                    <Space>
                        <Text type="secondary">
                            Language: <Text strong>{userPreferences.language}</Text>
                        </Text>
                        <Text type="secondary">
                            Depth: <Text strong>{userPreferences.depth}</Text>
                        </Text>
                    </Space>
                </Card>
            )}

            {/* 操作按钮 */}
            {selectedKey && (
                <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
                    <Row gutter={[8, 8]}>
                        {actionButtons.map(button => (
                            <Col xs={12} sm={6} key={button.key}>
                                <Button
                                    type={button.type}
                                    icon={button.icon}
                                    onClick={button.action}
                                    disabled={isLoading}
                                    block
                                    style={{
                                        borderColor: button.color,
                                        color: button.type === 'primary' ? '#fff' : button.color,
                                        height: 40
                                    }}
                                >
                                    {button.label}
                                </Button>
                            </Col>
                        ))}
                    </Row>
                    
                    <Row style={{ marginTop: 8 }}>
                        <Col span={24}>
                            <Space>
                                <Button
                                    size="small"
                                    onClick={clearChatHistory}
                                    icon={<DeleteOutlined />}
                                >
                                    Clear History
                                </Button>
                                {isLoading && (
                                    <Button
                                        size="small"
                                        onClick={handleStopOutput}
                                        icon={<PauseCircleOutlined />}
                                        danger
                                    >
                                        Stop
                                    </Button>
                                )}
                                {paused && (
                                    <Button
                                        size="small"
                                        onClick={handleRestartOutput}
                                        icon={<PlayCircleOutlined />}
                                        type="primary"
                                    >
                                        Resume
                                    </Button>
                                )}
                            </Space>
                        </Col>
                    </Row>
                </Card>
            )}

            {/* 聊天内容区域 */}
            <div 
                ref={chatContainerRef}
                style={{ 
                    flex: 1, 
                    overflow: 'auto', 
                    padding: '0 8px',
                    position: 'relative'
                }}
                onScroll={handleScroll}
            >
                {cards.map((card, index) => (
                    <Card 
                        key={index} 
                        style={{ 
                            marginBottom: 16, 
                            borderRadius: 12,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                        size="small"
                        extra={
                            <Space>
                                {userInputs[index] && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {userInputs[index].message}
                                    </Text>
                                )}
                                <Button
                                    size="small"
                                    icon={<RedoOutlined />}
                                    onClick={() => handleRegenerate(index)}
                                    disabled={isLoading}
                                    title="Regenerate"
                                />
                                <Button
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleDelete(index)}
                                    disabled={isLoading}
                                    danger
                                    title="Delete"
                                />
                            </Space>
                        }
                    >
                        <div 
                            style={{ fontSize: fontSize, lineHeight: 1.6 }}
                            onContextMenu={handleContextMenu}
                        >
                            {regeneratingIndex === index ? (
                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Text type="secondary">Regenerating content...</Text>
                                </div>
                            ) : (
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm, remarkMath]}
                                    rehypePlugins={[rehypeKatex]}
                                    components={{
                                        code({ node, inline, className, children, ...props }) {
                                            const match = /language-(\w+)/.exec(className || '');
                                            return !inline && match ? (
                                                <SyntaxHighlighter
                                                    style={darcula}
                                                    language={match[1]}
                                                    PreTag="div"
                                                    {...props}
                                                >
                                                    {String(children).replace(/\n$/, '')}
                                                </SyntaxHighlighter>
                                            ) : (
                                                <code className={className} {...props}>
                                                    {children}
                                                </code>
                                            );
                                        }
                                    }}
                                >
                                    {card}
                                </ReactMarkdown>
                            )}
                        </div>
                    </Card>
                ))}

                {isLoading && regeneratingIndex === -1 && (
                    <Card style={{ marginBottom: 16, borderRadius: 12 }} size="small">
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Text type="secondary">AI is thinking...</Text>
                        </div>
                    </Card>
                )}

                <div ref={chatEndRef} />

                {/* 滚动到底部按钮 */}
                {showScrollToBottom && (
                    <Button
                        type="primary"
                        shape="circle"
                        icon={<DownOutlined />}
                        onClick={forceScrollToBottom}
                        style={{
                            position: 'fixed',
                            bottom: showInput ? 120 : 80,
                            right: 24,
                            zIndex: 1000,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }}
                    />
                )}
            </div>

            {/* 输入区域 */}
            {showInput && (
                <Card size="small" style={{ marginTop: 8, borderRadius: 8 }}>
                    <Row gutter={8}>
                        <Col flex={1}>
                            <TextArea
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                placeholder="Ask a follow-up question..."
                                autoSize={{ minRows: 1, maxRows: 4 }}
                                onPressEnter={(e) => {
                                    if (!e.shiftKey) {
                                        e.preventDefault();
                                        handleButtonClick();
                                    }
                                }}
                            />
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                icon={<SendOutlined />}
                                onClick={handleButtonClick}
                                disabled={!inputValue.trim() || isLoading}
                                style={{ height: '100%' }}
                            >
                                Send
                            </Button>
                        </Col>
                    </Row>
                </Card>
            )}

            <ContextMenu 
                visible={contextMenuVisible}
                position={contextMenuPosition}
                onClose={() => setContextMenuVisible(false)}
                onClarify={handleClarify}
            />
        </div>
    );
};

export default Chat;
